// Two-phase S3 upload (SAFE-323): presigned PUT via the backend, then
// finalize with a content hash. Mirrors simple-lms's dropzone flow.
import { client, unwrap } from './client';
import { sha256Hex } from './sha256';

export async function uploadFile(
  file: File,
  folder: string,
  tag: string,
  /** Reports upload progress (0–100) of the PUT to S3. */
  onProgress?: (percent: number) => void,
): Promise<string> {
  const bytes = await file.arrayBuffer();
  // sha256Hex uses crypto.subtle when available (secure context) and a pure-JS
  // fallback otherwise — e.g. dev over plain HTTP on a LAN IP, where
  // crypto.subtle is undefined.
  const hash = await sha256Hex(bytes);

  const created = await unwrap(
    client.POST('/api/v1/uploads', {
      body: {
        filename: file.name,
        folder,
        tag,
        contentType: file.type || null,
        metadata: { hash },
      },
    }),
  );

  // XHR rather than fetch so we get upload progress events for the dropzone.
  await new Promise<void>((resolve, reject) => {
    const req = new XMLHttpRequest();
    req.upload.onprogress = (ev) => {
      if (ev.lengthComputable) onProgress?.((ev.loaded / ev.total) * 100);
    };
    req.onload = () => {
      if (req.status >= 200 && req.status < 300) {
        onProgress?.(100);
        resolve();
      } else {
        reject(new Error(`Upload failed: ${req.status} ${req.statusText}`));
      }
    };
    req.onerror = () => reject(new Error('Upload failed: network error'));
    req.open('PUT', created.uploadUrl);
    if (file.type) req.setRequestHeader('Content-Type', file.type);
    req.send(file);
  });

  await unwrap(
    client.POST('/api/v1/uploads/{id}/finalize', {
      params: { path: { id: created.id } },
      body: { hash },
    }),
  );

  return created.publicUrl;
}
